import * as React from 'react'
import { Text, StackChildren, InlineChildren } from '@doordash/design-language'
import Button from '@doordash/component-button'

import { withRouter, RouteComponentProps } from 'react-router-dom'

import LinkButton from '../../atoms/link-button'
import routes from '../../../routes'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'

import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'

import {
  AddCardError,
  ButtonContainer,
  CVVStyling,
  FormContainer,
  SmallFormRow,
  NumberStyling,
  InputContainer,
  AddCardErrorContainer,
  CvcIconButton,
  PaddedLabel,
  CancelButtonContainer,
  DesktopButton,
  MobileButton,
} from './styles'

import t from './strings'

import { NUMBER_ID, CVC_ID, EXPIRY_ID } from './constants'
import CvcModal from './cvc-modal'

import { detectWebviewOs } from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'

import { connect } from 'react-redux'
import * as actions from '../../../actions'

type FormProps = {
  uniqueLink: string
  dasherId: string
}

type ReduxProps = {
  submitFastPaySetup: (
    dasherId: string,
    stripeToken: string,
    mfaEnable?: boolean
  ) => void
  trackFastPaySetup?: (data: DA.FastPaySetup) => Promise<any>
}

const StripeCardForm: React.FC<FormProps &
  ReduxProps &
  RouteComponentProps> = ({
  uniqueLink,
  dasherId,
  submitFastPaySetup,
  trackFastPaySetup,
  history,
}) => {
  const [error, setError] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isShowingModal, setIsShowingModal] = React.useState(false)

  const elements = useElements()
  const stripe = useStripe()

  const isWebview = detectWebviewOs() !== OsTypes.NONE

  // Handle real-time validation errors from the card Element.
  const handleChange = event => {
    if (event.error) {
      setError(event.error.message)
    } else {
      setError(null)
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return
    }

    trackFastPaySetup({
      action: DA.EventActions.Submit,
      name: DA.FastPayNames.AddDebitCard,
    })

    setIsLoading(true)

    const card = elements.getElement(CardNumberElement)
    const result = await stripe.createToken(card, { currency: 'usd' })

    if (result.error) {
      // Inform the user if there was an error.
      setError(result.error.message)
      trackFastPaySetup({
        action: DA.EventActions.Error,
        name: DA.FastPayNames.AddDebitCard,
        value: result.error.message,
      })
    } else {
      setError(null)

      // Submit Debit Card for Fast Pay
      try {
        await submitFastPaySetup(dasherId, result.token.id)
        await history.push(
          isWebview
            ? routes.webview.buildPath({ sub: '' })
            : routes.dasherflow.buildPath({
                uniqueLink,
                step: 'dropshipping',
              })
        )
      } catch (error) {
        setError(error)
      }
    }

    setIsLoading(false)
  }

  const handleCardFieldFocus = (fieldValue: DA.FastPayValues) => {
    trackFastPaySetup({
      action: DA.EventActions.Clicked,
      name: DA.FastPayNames.CardInfoFields,
      value: fieldValue,
    })
  }

  const handleModalClick = (modalName: DA.FastPayNames) => (
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation()

    trackFastPaySetup({
      action: DA.EventActions.Clicked,
      name: modalName,
      value: isShowingModal ? DA.FastPayValues.Closed : DA.FastPayValues.Opened,
    })

    setIsShowingModal(!isShowingModal)
  }

  const handleCancelClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    trackFastPaySetup({
      action: DA.EventActions.Clicked,
      name: DA.FastPayNames.AddDebitCard,
      value: DA.FastPayValues.Cancel,
    })
  }

  return (
    <FormContainer>
      <StackChildren size={StackChildren.Sizes.XxSmall}>
        <InlineChildren>
          <NumberStyling>
            <PaddedLabel htmlFor={NUMBER_ID}>
              {t.debitCard__label()}
            </PaddedLabel>
            <InputContainer hasError={!!error}>
              <CardNumberElement
                id={NUMBER_ID}
                onChange={handleChange}
                onFocus={() =>
                  handleCardFieldFocus(DA.FastPayValues.DebitCardNumber)
                }
              />
            </InputContainer>
          </NumberStyling>
        </InlineChildren>
        <SmallFormRow>
          <CVVStyling>
            <PaddedLabel htmlFor={EXPIRY_ID}>{t.expDate__label()}</PaddedLabel>
            <InputContainer hasError={!!error}>
              <CardExpiryElement
                id={EXPIRY_ID}
                onChange={handleChange}
                onFocus={() => handleCardFieldFocus(DA.FastPayValues.ExpDate)}
              />
            </InputContainer>
          </CVVStyling>

          <CVVStyling>
            <Text tag="label" styles={Text.Styles.Medium14} htmlFor={CVC_ID}>
              <InlineChildren alignItems={InlineChildren.Alignment.Center}>
                <div>{t.cvcNumber__label()}</div>
                <CvcIconButton
                  onClick={handleModalClick(DA.FastPayNames.CvcInfoModal)}
                />
              </InlineChildren>
            </Text>
            <InputContainer hasError={!!error}>
              <CardCvcElement
                id={CVC_ID}
                onChange={handleChange}
                onFocus={() => handleCardFieldFocus(DA.FastPayValues.CvcNumber)}
              />
            </InputContainer>
          </CVVStyling>
        </SmallFormRow>

        <AddCardErrorContainer>
          <AddCardError hasError={!!error}>
            <Text>{error}</Text>
          </AddCardError>
        </AddCardErrorContainer>

        <ButtonContainer>
          <DesktopButton>
            <Button
              onClick={handleSubmit}
              isInline
              type={Button.Types.PrimaryPill}
              state={isLoading ? Button.States.Loading : Button.States.Default}
              isDisabled={!stripe}
            >
              {t.addDebitCard__primaryAction()}
            </Button>
          </DesktopButton>
          <MobileButton>
            <Button
              onClick={handleSubmit}
              type={Button.Types.PrimaryPill}
              state={isLoading ? Button.States.Loading : Button.States.Default}
              isDisabled={!stripe}
            >
              {t.addDebitCard__primaryAction()}
            </Button>
          </MobileButton>
          <CancelButtonContainer>
            <LinkButton
              type={Button.Types.FlatSecondary}
              isInline
              onClick={handleCancelClick}
              href={
                isWebview
                  ? routes.webview.buildPath({ sub: '' })
                  : routes.dasherflow.buildPath({
                      uniqueLink,
                      step: 'dropshipping',
                    })
              }
            >
              {t.addDebitCard__secondaryAction()}
            </LinkButton>
          </CancelButtonContainer>
          {isShowingModal && (
            <CvcModal
              onClose={handleModalClick(DA.FastPayNames.CvcInfoModal)}
            />
          )}
        </ButtonContainer>
      </StackChildren>
    </FormContainer>
  )
}

const mapDispatchToProps = {
  submitFastPaySetup: actions.submitFastPaySetup,
  trackFastPaySetup: events.trackFastPaySetup,
}

export default connect(null, mapDispatchToProps)(withRouter(StripeCardForm))
